import { generatePath, useHistory, useParams } from "react-router-dom";
import ErrorComponent from "@components/ui/error";
import { ROUTES } from "@/routes_new";
import { stringifyUrl, withDefaultDates } from "@/utils";
import { useFilterParamsV4 } from "@/hooks/useFilterParams";
import { useI18n } from "@/contexts/i18nContext";
import { differenceInDays, isSameHour } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { getSearchMentionsV4 } from "@/api/endpoints/searches";
import MentionCardNew from "@components/MentionCardNew";
import { MentionCardsProvider } from "@/containers/MentionCardsContext";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@components/ui/sheet";
import {
  formatDateFromMCPMonitorTimeSeriesGranularity,
  getMCPMonitorTimeSeriesGranularityFromDayDiff,
} from "@components/widgets/utils";
import { plural } from "@lingui/core/macro";
import { useEffect, useState } from "react";
import PaginationNew from "@components/PaginationNew";
import { AnimatePresence, motion } from "motion/react";
import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@components/ui/empty";
import { MessagesSquareIcon } from "lucide-react";
import { Trans, useLingui } from "@lingui/react/macro";
import { Skeleton } from "@components/ui/skeleton";
import { useAnimationsSettled } from "@/hooks/useAnimationsSettled";

export function MentionsSheetView() {
  const { formatDateRange, formatDate } = useI18n();
  const { t } = useLingui();

  // Refs
  const [contentRef, sheetSettled] = useAnimationsSettled();

  // URL state
  const {
    uuid: searchUuid,
    orgUuid,
    startDate,
    endDate,
  } = useParams<{
    startDate: string;
    endDate: string;
    orgUuid: Uuid;
    uuid: Uuid;
  }>();

  const [_filters] = useFilterParamsV4();
  const history = useHistory();

  const filters = withDefaultDates(_filters);

  // Local state
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);

  // Server state
  const {
    data: mentionCardsData,
    error: mentionCardsError,
    isLoading: isMentionCardsLoading,
  } = useQuery({
    queryKey: [searchUuid, "mentions", startDate, endDate, filters, page],
    queryFn: () =>
      getSearchMentionsV4(searchUuid, {
        ...filters,
        start_date: startDate,
        end_date: endDate,
        full_count: true,
        page,
      }),
  });

  // Helpers
  const count = mentionCardsData?.ok ? mentionCardsData.data.count : 0;

  const pageCount = mentionCardsData?.ok ? mentionCardsData.data.page_count : 0;

  const diffInDays = differenceInDays(filters.end_date, filters.start_date);
  const granularity = getMCPMonitorTimeSeriesGranularityFromDayDiff(diffInDays);

  const lede = formatDateRange(new Date(startDate), new Date(endDate), "PPP", {
    allowSameDay: isSameHour(startDate, endDate),
  });

  // Handlers
  function handleOpenChangeComplete(newIsOpen: boolean) {
    if (newIsOpen) {
      return;
    }

    history.replace(
      stringifyUrl({
        url: generatePath(ROUTES.MENTION_DETAIL_ORGID, {
          uuid: searchUuid,
          orgUuid,
        }),
        query: _filters,
      })
    );
  }

  // Side-effects
  useEffect(() => {
    const frame = requestAnimationFrame(() => setIsOpen(true));

    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <Sheet
      open={isOpen}
      onOpenChange={setIsOpen}
      onOpenChangeComplete={handleOpenChangeComplete}
    >
      <SheetContent ref={contentRef}>
        <SheetHeader>
          <SheetTitle>
            {formatDateFromMCPMonitorTimeSeriesGranularity(
              startDate,
              granularity,
              formatDate
            )}
          </SheetTitle>
          <SheetDescription>
            {count} {plural(count, { one: "inlägg", other: "inlägg" })}
            {lede ? ` · ${lede}` : null}
          </SheetDescription>
        </SheetHeader>

        {sheetSettled && (
          <div className="overflow-y-auto px-6 pb-6 flex-1 pt-px">
            <AnimatePresence mode="popLayout">
              {mentionCardsData ? (
                mentionCardsData.ok ? (
                  <motion.div
                    className="flex flex-col gap-5"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    key="mentions"
                  >
                    {mentionCardsData.data.results.length ? (
                      <MentionCardsProvider>
                        {mentionCardsData.data.results.map((mentionCard) => {
                          return (
                            <MentionCardNew
                              key={mentionCard.episode.id}
                              mentionCard={mentionCard}
                              orgUuid={orgUuid}
                              tags={[]}
                            />
                          );
                        })}

                        <PaginationNew
                          page={page}
                          max={pageCount}
                          onChangePage={setPage}
                        />
                      </MentionCardsProvider>
                    ) : (
                      <Empty>
                        <EmptyHeader>
                          <EmptyMedia variant="icon">
                            <MessagesSquareIcon />
                          </EmptyMedia>
                          <EmptyTitle>
                            <Trans>Inga omnämnanden</Trans>
                          </EmptyTitle>
                        </EmptyHeader>
                      </Empty>
                    )}
                  </motion.div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    key="error"
                  >
                    <ErrorComponent
                      heading={t`Åh nej!`}
                      description={t`Något gick snett med att ladda omnämnanden. Vänligen försök igen senare.`}
                    />
                  </motion.div>
                )
              ) : isMentionCardsLoading ? (
                <motion.div
                  className="flex flex-col gap-5"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  key="skeletons"
                >
                  <Skeleton className="w-full h-64" />
                  <Skeleton className="w-full h-64" />
                  <Skeleton className="w-full h-64" />
                </motion.div>
              ) : mentionCardsError ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  key="error"
                >
                  <ErrorComponent
                    heading={t`Åh nej!`}
                    description={t`Något gick snett med att ladda omnämnanden. Vänligen försök igen senare.`}
                  />
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
